import { BurgerMenuBars } from "@/components/icons/burger-menu-bars";
import { HbLogo } from "@/components/icons/hb-logo";
import { type NewsletterTranslations } from "@/components/layout/footer/translations";
import { HeaderNavigationEntry } from "@/components/layout/header";
import { MobileMenuContextProvider } from "@/components/layout/header/mobile-menu-context-provider";
import MobileMenuDialog from "@/components/layout/header/mobile-menu-dialog";
import { type NavbarTranslations } from "@/components/layout/header/translations";
import { Locale } from "@/i18n/i18n.config";
import { useTranslations } from "@/libs/base/components/locale-provider";
import TranslatedLink from "@/libs/base/components/translated-link";
import { cn } from "@/libs/base/utils/layout";
import { Dialog, DialogPanel, Transition, TransitionChild } from "@headlessui/react";
import { Fragment, useState } from "react";

interface PageProps {
  navigationEntries: HeaderNavigationEntry[];
  locale: Locale;
  selectableLocales: Locale[];
  selectLocale: (locale: Locale) => void;
  isBigHeaderShown: boolean;
  region: string;
  navbarTranslations: NavbarTranslations;
  newsletterTranslations: NewsletterTranslations;
}

export default function MobileHeader({
  navigationEntries,
  locale,
  selectableLocales,
  selectLocale,
  isBigHeaderShown,
  region,
  navbarTranslations,
  newsletterTranslations,
}: PageProps) {
  const bigHeader = {
    width: 53,
    height: 73,
  };
  const smallHeader = {
    width: 29,
    height: 40,
  };
  const [isBurgerMenuShown, setIsBurgerMenuShown] = useState(false);
  const { navbar: translations } = useTranslations();

  return (
    <>
      {/* burger menu */}
      <Transition show={isBurgerMenuShown} as={Fragment}>
        <Dialog as="div" className="relative z-40 lg:hidden" onClose={setIsBurgerMenuShown}>
          <TransitionChild
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </TransitionChild>

          <div className="fixed bottom-0 right-0 top-0 z-40 flex">
            <TransitionChild
              as={Fragment}
              enter="transition ease-in-out duration-300 transform"
              enterFrom="translate-x-full"
              leave="transition ease-in-out duration-300 transform"
              leaveTo="translate-x-full"
            >
              <DialogPanel className="relative flex w-full flex-col overflow-y-auto overflow-x-hidden bg-white shadow-xl sm:max-w-sm">
                <MobileMenuContextProvider close={() => setIsBurgerMenuShown(false)}>
                  <MobileMenuDialog
                    closeMenuDialog={() => setIsBurgerMenuShown(false)}
                    selectLocale={(locale) => selectLocale(locale)}
                    navigationEntries={navigationEntries}
                    region={region}
                    locale={locale}
                    selectableLocales={selectableLocales}
                    navbarTranslations={navbarTranslations}
                    newsletterTranslations={newsletterTranslations}
                  />
                </MobileMenuContextProvider>
              </DialogPanel>
            </TransitionChild>
          </div>
        </Dialog>
      </Transition>

      {/* header bar */}
      <header className="fixed top-0 z-20 block w-full bg-white lg:hidden">
        <nav
          aria-label="Top"
          className={cn(
            "flex w-full items-center justify-between border-b border-gray-200",
            "transition-[height] duration-[256ms] ease-[ease]",
            isBigHeaderShown ? "h-[100px]" : "h-[50px]",
          )}
        >
          {/* Logo */}
          <TranslatedLink href="/" className="flex flex-1 items-center px-2">
            <span className="sr-only">Hering Berlin</span>
            <HbLogo
              width={isBigHeaderShown ? bigHeader.width : smallHeader.width}
              height={isBigHeaderShown ? bigHeader.height : smallHeader.height}
              showBrandName={isBigHeaderShown}
              className={cn("transition-all duration-[256ms] ease-[ease]", !isBigHeaderShown && "mt-2")}
            />
          </TranslatedLink>

          {/* Burger menu button for smaller screens */}
          <button
            type="button"
            className="relative rounded-md bg-white p-2 text-black transition-colors duration-300 hover:text-gray-500"
            onClick={() => setIsBurgerMenuShown(true)}
          >
            <span className="sr-only">{translations.open_menu_sr}</span>
            <BurgerMenuBars className="h-6 w-6" aria-hidden="true" />
          </button>
        </nav>
      </header>
    </>
  );
}
